import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOperationalContext } from '../../contexts/OperationalContext';

interface OperationalBreadcrumbProps {
  className?: string;
  mode?: 'full' | 'execution_only' | 'workspace_only';
}

export const OperationalBreadcrumb: React.FC<OperationalBreadcrumbProps> = ({
  className,
  mode = 'full',
}) => {
  const navigate = useNavigate();
  const {
    activeFlowId,
    activeFlowName,
    activeExecutionId,
    activeExecutionCode,
    activeStageId,
    activeStageName,
  } = useOperationalContext();

  const flowLabel = activeFlowName ?? 'Fluxo';
  const executionLabel = activeExecutionCode ?? 'Execução';
  const stageLabel = activeStageName ?? 'Etapa';

  const fullCrumbs = [
    {
      key: 'flow',
      label: flowLabel,
      onClick: activeFlowId
        ? () => navigate('/centro-operacional')
        : undefined,
    },
    {
      key: 'execution',
      label: executionLabel,
      onClick: activeExecutionId
        ? () =>
            navigate(
              `/execucoes/instancias/${encodeURIComponent(activeExecutionId)}?executionCode=${encodeURIComponent(executionLabel)}&flowId=${encodeURIComponent(activeFlowId ?? '')}&flowName=${encodeURIComponent(flowLabel)}`,
            )
        : undefined,
    },
    {
      key: 'stage',
      label: stageLabel,
      onClick: activeStageId && activeExecutionId
        ? () =>
            navigate(
              `/execucoes/instancias/${encodeURIComponent(activeExecutionId)}?stageId=${encodeURIComponent(activeStageId)}&stageName=${encodeURIComponent(stageLabel)}&flowId=${encodeURIComponent(activeFlowId ?? '')}&flowName=${encodeURIComponent(flowLabel)}`,
            )
        : undefined,
    },
  ];

  const executionOnlyCrumbs = [
    activeExecutionId
      ? {
          key: 'execution',
          label: executionLabel,
          onClick: () =>
            navigate(
              `/execucoes/instancias/${encodeURIComponent(activeExecutionId)}?executionCode=${encodeURIComponent(executionLabel)}`,
            ),
        }
      : null,
    activeStageId && activeExecutionId && activeStageName
      ? {
          key: 'stage',
          label: stageLabel,
          onClick: () =>
            navigate(
              `/execucoes/instancias/${encodeURIComponent(activeExecutionId)}?stageId=${encodeURIComponent(activeStageId)}&stageName=${encodeURIComponent(stageLabel)}&executionCode=${encodeURIComponent(executionLabel)}`,
            ),
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onClick?: () => void }>;

  const workspaceOnlyCrumbs = [
    {
      key: 'workspace',
      label: 'Centro Operacional',
      onClick: () => navigate('/centro-operacional'),
    },
    {
      key: 'monitor',
      label: 'Fluxo',
      onClick: () => navigate('/centro-operacional?tab=flow'),
    },
  ];

  const crumbs = mode === 'execution_only'
    ? executionOnlyCrumbs
    : mode === 'workspace_only'
      ? workspaceOnlyCrumbs
      : fullCrumbs;

  return (
    <div className={`flex min-w-0 items-center gap-1 text-[10px] font-normal tracking-[0.08em] text-[#708396] dark:text-slate-500 ${className ?? ''}`.trim()}>
      {crumbs.map((crumb, index) => (
        <React.Fragment key={crumb.key}>
          {index > 0 && <ChevronRight size={10} className="shrink-0 text-[#445564] dark:text-slate-600" />}
          <button
            type="button"
            onClick={crumb.onClick}
            disabled={!crumb.onClick}
            className={`min-w-0 rounded-sm px-1 py-0.5 text-[10px] font-normal transition-colors ${
              crumb.onClick
                ? 'text-[#7d90a4] hover:bg-[#111a22] hover:text-[#c9d6e2] dark:text-slate-400 dark:hover:bg-slate-800/70 dark:hover:text-slate-200'
                : 'cursor-default text-[#5f7182] dark:text-slate-500'
            }`}
          >
            <span className="block truncate">{crumb.label}</span>
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

export default OperationalBreadcrumb;

